/* eslint-disable no-underscore-dangle */
import { Box, Loader, TextInput } from '@mantine/core';
import { IconAlertCircle, IconClock, IconInbox, IconPencil, IconSearch, IconSend, IconTrash } from '@tabler/icons';
import { FC, useContext, useEffect } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedState } from '@mantine/hooks';
import NavigationLink from '../../Atoms/Navigation/NavigationLink';
import { useNavigationItem } from '../../Organismns/Navigation/NavigationItemHook';
import { WindowContext } from '../../Service/Context/WindowContext';
import { PostboxCountersContext } from '../../Service/Context/PostboxCountersContext';
import { ThemeTitle } from '../../Atoms/ThemeText';
import { EmailBoxContext } from '../../Service/Context/EmailBoxContext';
import EmailBoxCounter from './EmailBoxCounter';
import MailOutboundConfigNav from './imap/MailOutboundConfigNav';

const EmailInboxNavLinks: FC<{ closeNavBar: () => void }> = ({ closeNavBar }) => {
  const navigate: NavigateFunction = useNavigate();
  const { pathname } = useLocation();
  const { sm } = useContext(WindowContext);
  const { isBookitupScope, openFolder, searchQuery, updateQuery, loading } = useContext(EmailBoxContext);
  const { unreadCount, failedCount, scheduledCount } = useContext(PostboxCountersContext);
  const [localQuery, setLocalQuery] = useDebouncedState(searchQuery, 500);

  useEffect(() => {
    if (localQuery !== searchQuery && updateQuery) {
      updateQuery(localQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuery]);

  const _openFolder = (name: string, link: string, totalMessages?: number | null) => {
    openFolder({
      name,
      src: 'BOOKITUP',
      totalMessages,
    });
    navigate(link);
    closeNavBar();
  };

  return (
    <>
      {!sm && (
        <ThemeTitle p="md" order={3}>
          Postfach
        </ThemeTitle>
      )}
      <Box p="xs">
        <TextInput
          defaultValue={localQuery}
          onChange={(e) => setLocalQuery(e.currentTarget.value)}
          icon={<IconSearch size={16} />}
          placeholder="E-mails durchsuchen..."
          variant="filled"
          disabled={loading && !isBookitupScope}
          rightSection={loading ? <Loader size="xs" /> : null}
        />
      </Box>
      <NavigationLink opened label="Posteingang" icon={sm && <IconInbox size={16} />} childrenOffset={28}>
        <NavigationLink
          label="Alle"
          active={pathname === '/postbox/inbox' && isBookitupScope}
          onClick={() => _openFolder('Alle', '/postbox/inbox')}
          variant="filled"
        />
        <NavigationLink
          label="Unerledigt"
          active={useNavigationItem('/postbox/inbox/unread') && isBookitupScope}
          onClick={() => _openFolder('Unerledigt', '/postbox/inbox/unread', unreadCount)}
          variant="filled"
          rightSection={<EmailBoxCounter count={unreadCount} />}
        />
        <NavigationLink
          label="ohne Eventbezug"
          active={useNavigationItem('/postbox/inbox/unassigned') && isBookitupScope}
          onClick={() => _openFolder('ohne Eventbezug', '/postbox/inbox/unassigned', null)}
          variant="filled"
        />
      </NavigationLink>
      <NavigationLink
        label="Gesendet"
        icon={sm && <IconSend size={16} />}
        active={useNavigationItem('/postbox/outbound') && isBookitupScope}
        onClick={() => _openFolder('Gesendet', '/postbox/outbound')}
        variant="filled"
      />
      <NavigationLink
        label="Entwürfe"
        icon={sm && <IconPencil size={16} />}
        active={useNavigationItem('/postbox/drafts') && isBookitupScope}
        onClick={() => _openFolder('Entwürfe', '/postbox/drafts', null)}
        variant="filled"
      />
      <NavigationLink
        label="Geplant"
        icon={sm && <IconClock size={16} />}
        active={useNavigationItem('/postbox/scheduled') && isBookitupScope}
        onClick={() => _openFolder('Geplant', '/postbox/scheduled', null)}
        variant="filled"
        rightSection={<EmailBoxCounter count={scheduledCount} />}
      />
      <NavigationLink
        label="Fehlgeschlagen"
        icon={sm && <IconAlertCircle size={16} />}
        active={useNavigationItem('/postbox/failed') && isBookitupScope}
        onClick={() => _openFolder('Fehlgeschlagen', '/postbox/failed', failedCount)}
        variant="filled"
        rightSection={<EmailBoxCounter count={failedCount} />}
      />
      <NavigationLink
        label="Papierkorb"
        icon={sm && <IconTrash size={16} />}
        active={useNavigationItem('/postbox/trash') && isBookitupScope}
        onClick={() => _openFolder('Papierkorb', '/postbox/trash', null)}
        variant="filled"
      />

      <MailOutboundConfigNav closeNavBar={closeNavBar} />
    </>
  );
};

export default EmailInboxNavLinks;
